import { useState } from 'react'
import { Images, Shirt, Sparkles, Trash2 } from 'lucide-react'
import { useQuoteSubItemApi } from 'src/api/db/apiQuoteSubItem'
import { MockupGeneratingOverlay } from 'src/shared/components/MockupGeneratingOverlay'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'src/shared/components/ui/dialog'
import { Image } from 'src/shared/components/ui'
import { cn } from 'src/shared/components/ui/utils'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { MockupGenerateForm } from './MockupGenerateForm'

const ROTATIONS = [-3.5, 2.5, -2, 3, -2.5, 1.5]

const DOT_GRID_STYLE = {
  backgroundImage:
    'radial-gradient(circle, rgb(0 0 0 / 0.1) 1px, transparent 1px)',
  backgroundSize: '18px 18px',
} as const

export const MockupGalleryDialog = ({
  open,
  onOpenChange,
  mockupUrls,
  productName,
  quoteSubItemId,
  designUrl,
  canGenerate = true,
  allowDelete = true,
  disabledReason,
  isGenerating = false,
  onGenerate,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  mockupUrls: string[]
  productName: string
  quoteSubItemId: number
  designUrl?: string | null
  canGenerate?: boolean
  allowDelete?: boolean
  disabledReason?: string
  isGenerating?: boolean
  onGenerate?: (params: { logoUrl: string; customPrompt: string }) => void
}) => {
  const { t } = useLocalization()
  const { updateQuoteSubItem } = useQuoteSubItemApi()
  const [featuredUrl, setFeaturedUrl] = useState<string | null>(null)
  const [deletingUrl, setDeletingUrl] = useState<string | null>(null)
  const [showGenerateMore, setShowGenerateMore] = useState(false)

  const hasMockups = mockupUrls.length > 0
  const featured =
    featuredUrl && mockupUrls.includes(featuredUrl)
      ? featuredUrl
      : (mockupUrls[0] ?? null)

  const handleDelete = (url: string) => {
    if (deletingUrl) return
    setDeletingUrl(url)
    const nextUrls = mockupUrls.filter((u) => u !== url)
    updateQuoteSubItem.mutate(
      { id: quoteSubItemId, mockup_urls: nextUrls },
      {
        onSettled: () => setDeletingUrl(null),
        onSuccess: () => {
          if (featuredUrl === url) setFeaturedUrl(nextUrls[0] ?? null)
        },
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[min(85vh,820px)] max-w-xl flex-col gap-0 overflow-hidden border-gray-200/80 p-0 shadow-xl sm:rounded-[20px]"
        showClose
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader className="relative z-10 shrink-0 px-5 pb-3 pt-5 text-left">
          <Row className="items-center gap-2.5 pr-8">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 shadow-sm">
              <Sparkles className="h-3.5 w-3.5 text-white" strokeWidth={2} />
            </div>
            <Column className="min-w-0 gap-0">
              <DialogTitle className="text-base leading-tight">
                {t('Mockups', 'Mockups')}
              </DialogTitle>
              <DialogDescription className="truncate text-[12px] text-gray-500">
                {productName}
                {hasMockups ? ` · ${mockupUrls.length}` : ''}
              </DialogDescription>
            </Column>
          </Row>
        </DialogHeader>

        <Column className="min-h-0 flex-1 gap-0 overflow-y-auto">
          {isGenerating && (
            <div
              className="relative min-h-[260px] bg-gray-50"
              style={DOT_GRID_STYLE}
            >
              <div
                className="pointer-events-none absolute -left-8 top-4 h-36 w-36 rounded-full bg-violet-300/25 blur-3xl"
                aria-hidden
              />
              <div
                className="pointer-events-none absolute -right-6 bottom-6 h-32 w-32 rounded-full bg-sky-300/25 blur-3xl"
                aria-hidden
              />
              <MockupGeneratingOverlay
                loadingText={t('Developing shots…', 'Framkallar bilder…')}
              />
            </div>
          )}

          {!isGenerating && !hasMockups && onGenerate && (
            <div className="relative px-5 pb-5 pt-1" style={DOT_GRID_STYLE}>
              <div
                className="pointer-events-none absolute -right-10 -top-6 h-40 w-40 rounded-full bg-violet-300/30 blur-3xl"
                aria-hidden
              />
              <div
                className="pointer-events-none absolute -bottom-10 -left-8 h-36 w-36 rounded-full bg-sky-300/25 blur-3xl"
                aria-hidden
              />

              <Column className="relative gap-4">
                <Column className="items-center gap-2 px-2 pb-1 pt-3 text-center">
                  <Row className="mb-1 items-end justify-center gap-2.5">
                    {(
                      [
                        {
                          Icon: Shirt,
                          gradient: 'from-violet-400 to-purple-600',
                          rotate: -8,
                        },
                        {
                          Icon: Sparkles,
                          gradient: 'from-sky-400 to-blue-500',
                          rotate: 0,
                          featured: true,
                        },
                        {
                          Icon: Images,
                          gradient: 'from-fuchsia-400 to-pink-500',
                          rotate: 7,
                        },
                      ] as const
                    ).map(({ Icon, gradient, rotate, ...rest }) => (
                      <div
                        key={gradient}
                        className={cn(
                          'flex items-center justify-center rounded-2xl bg-gradient-to-br shadow-md',
                          gradient,
                          'featured' in rest
                            ? '-mb-1.5 h-11 w-11'
                            : 'h-10 w-10'
                        )}
                        style={{ transform: `rotate(${rotate}deg)` }}
                      >
                        <Icon
                          className="h-4 w-4 text-white"
                          strokeWidth={1.75}
                        />
                      </div>
                    ))}
                  </Row>
                  <p className="text-sm font-medium text-gray-900">
                    {t('Bring the design to life', 'Ge liv åt designen')}
                  </p>
                  <p className="max-w-[280px] text-[12px] leading-relaxed text-gray-500">
                    {t(
                      'AI mockups show your artwork on the real product — ready to share in about 15 seconds.',
                      'AI-mockups visar din design på den riktiga produkten — klar att dela på cirka 15 sekunder.'
                    )}
                  </p>
                </Column>

                <div className="relative overflow-hidden rounded-2xl border border-white/70 bg-white/80 p-4 shadow-[0_16px_40px_-12px_rgba(99,102,241,0.18)] backdrop-blur-md">
                  <MockupGenerateForm
                    designUrl={designUrl}
                    onGenerate={onGenerate}
                    isPending={isGenerating}
                    canGenerate={canGenerate}
                    disabledReason={disabledReason}
                    hasMockups={false}
                  />
                </div>
              </Column>
            </div>
          )}

          {!isGenerating && hasMockups && (
            <>
              <div
                className="relative shrink-0 bg-gray-50 px-5 pb-3 pt-2"
                style={DOT_GRID_STYLE}
              >
                <div
                  className="pointer-events-none absolute left-1/2 top-0 h-28 w-48 -translate-x-1/2 rounded-full bg-violet-200/30 blur-3xl"
                  aria-hidden
                />
                {featured && (
                  <div className="relative mx-auto max-w-[400px] overflow-hidden rounded-xl border border-white bg-white p-2.5 shadow-[0_12px_32px_-8px_rgba(15,23,42,0.18)] ring-1 ring-black/5">
                    <Image
                      src={featured}
                      alt={t('Featured mockup', 'Utvald mockup')}
                      className="mx-auto max-h-[min(48vh,420px)] w-full rounded-md bg-white object-contain"
                      withLens
                    />
                  </div>
                )}
              </div>

              <div
                className="bg-gray-50 px-4 pb-4 pt-2"
                style={DOT_GRID_STYLE}
              >
                <div className="flex flex-wrap items-end justify-center gap-2">
                  {mockupUrls.map((url, index) => {
                    const isFeatured = url === featured
                    const isDeleting = deletingUrl === url
                    const rotation = ROTATIONS[index % ROTATIONS.length]
                    return (
                      <div
                        key={url}
                        className={cn(
                          'group relative w-[68px] shrink-0',
                          'animate-in fade-in zoom-in-95 duration-300 fill-mode-both',
                          isDeleting && 'pointer-events-none opacity-40'
                        )}
                        style={{ animationDelay: `${index * 45}ms` }}
                      >
                        <div style={{ transform: `rotate(${rotation}deg)` }}>
                          <button
                            type="button"
                            onClick={() => setFeaturedUrl(url)}
                            className={cn(
                              'block w-full overflow-hidden rounded-md border bg-white p-1 shadow-sm transition-all',
                              isFeatured
                                ? 'border-blue-500 shadow-md ring-2 ring-blue-500/25'
                                : 'border-white ring-1 ring-black/5 hover:shadow-md'
                            )}
                          >
                            <Image
                              src={url}
                              alt={t(
                                `Mockup ${index + 1}`,
                                `Mockup ${index + 1}`
                              )}
                              className="aspect-square w-full rounded-sm bg-white object-contain"
                            />
                          </button>
                        </div>

                        {allowDelete ? (
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className={cn(
                              'absolute -right-1.5 -top-1.5 z-10 h-6 w-6 rounded-full p-0 shadow-sm',
                              'opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100'
                            )}
                            aria-label={t('Delete mockup', 'Ta bort mockup')}
                            disabled={!!deletingUrl}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDelete(url)
                            }}
                          >
                            <Trash2 className="h-3 w-3 text-red-600" />
                          </Button>
                        ) : null}
                      </div>
                    )
                  })}
                </div>
              </div>

              {onGenerate && (
                <div className="border-t border-gray-100 bg-white px-5 py-3">
                  {showGenerateMore ? (
                    <Column className="gap-3">
                      <Row className="items-center justify-between">
                        <p className="text-[12px] font-medium text-gray-700">
                          {t('Generate more', 'Generera fler')}
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-[11px] text-gray-500"
                          onClick={() => setShowGenerateMore(false)}
                        >
                          {t('Hide', 'Dölj')}
                        </Button>
                      </Row>
                      <MockupGenerateForm
                        designUrl={designUrl}
                        onGenerate={onGenerate}
                        isPending={isGenerating}
                        canGenerate={canGenerate}
                        disabledReason={disabledReason}
                        hasMockups
                        compact
                      />
                    </Column>
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="h-9 w-full text-[12px]"
                      onClick={() => setShowGenerateMore(true)}
                    >
                      <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                      {t('Generate more', 'Generera fler')}
                    </Button>
                  )}
                </div>
              )}
            </>
          )}

          {!isGenerating && !hasMockups && !onGenerate && (
            <Column className="items-center justify-center gap-2 px-6 py-16">
              <Images className="h-8 w-8 text-gray-300" />
              <p className="text-sm text-gray-500">
                {t('No mockups yet', 'Inga mockups ännu')}
              </p>
            </Column>
          )}
        </Column>
      </DialogContent>
    </Dialog>
  )
}
